import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, ClipboardList, BookOpen, MessageCircleHeart, Feather,
  TrendingUp, HandHeart, Users, Sparkles, ArrowRight, ArrowLeft, X, Clock,
  HeartHandshake, LifeBuoy, UserCircle,
} from 'lucide-react';

// Member tour (default) — 8 steps + welcome.
const MEMBER_STEPS = [
  {
    icon: Sparkles,
    kicker: 'Welcome',
    title: 'Welcome to Brotherhood+',
    body: 'Brotherhood+ is your personal growth platform — designed to help you strengthen your mental wellness, build healthier relationships, and develop practical life skills through evidence-based learning and personalized AI support.\n\nYou don\u2019t need to use everything today. We\u2019ll guide you one step at a time.',
    href: null, primary: 'Start tour',
  },
  {
    icon: Home, kicker: 'Step 1 · Home',
    title: 'Your personal dashboard',
    body: 'Home is where each day starts. Every day, Brotherhood+ recommends your next best step based on your recent activity, goals, progress, and check-ins.',
    href: '/app',
  },
  {
    icon: ClipboardList, kicker: 'Step 2 · Daily Check-In',
    title: 'Under one minute a day',
    body: 'The more consistently you check in, the more personalized your recommendations, wellness insights, and AI coaching become.',
    href: '/app/checkin',
  },
  {
    icon: BookOpen, kicker: 'Step 3 · Brotherhood University',
    title: 'Evidence-based lessons',
    body: 'Learn contains lessons created by licensed mental health professionals. Each one is practical, concise, and designed to build real-life skills.',
    href: '/app/learn',
  },
  {
    icon: MessageCircleHeart, kicker: 'Step 4 · AI Coach',
    title: 'Apply what you learn',
    body: 'Ask questions. Reflect on challenges. Practice conversations. Receive personalized guidance. The more you use Brotherhood+, the more personalized your experience becomes.',
    href: '/app/coach',
  },
  {
    icon: Feather, kicker: 'Step 5 · Journal',
    title: 'Your private reflection space',
    body: 'Writing regularly can help you recognize emotional patterns, celebrate progress, and deepen your self-awareness.',
    href: '/app/journal',
  },
  {
    icon: TrendingUp, kicker: 'Step 6 · Progress',
    title: 'Track your growth over time',
    body: 'View check-ins, wellness trends, lessons completed, growth plans, and weekly wellness reviews.\n\nProgress is about consistency, not perfection.',
    href: '/app/progress',
  },
  {
    icon: HandHeart, kicker: 'Step 7 · Support',
    title: 'For loved ones supporting men',
    body: 'Supporting someone you care about? This section provides evidence-informed guidance for partners, parents, mentors, family members, and friends supporting the men they care about. It includes a dedicated Support Coach.',
    href: '/app/support',
  },
  {
    icon: Users, kicker: 'Step 8 · Find Therapy',
    title: 'Culturally responsive therapists',
    body: 'Search for licensed mental health professionals skilled in culturally responsive care for men of every background. Filter by state, insurance, specialty, language, and telehealth.',
    href: '/app/therapists',
  },
];

// Supporter tour — 7 steps + welcome. No University / Journal / Progress / Check-In.
const SUPPORTER_STEPS = [
  {
    icon: Sparkles,
    kicker: 'Welcome',
    title: 'Welcome to Brotherhood Support',
    body: 'Brotherhood Support was designed for the people who stand beside someone they care about.\n\nYou\u2019ll learn how to encourage without controlling, listen without judgment, recognize warning signs, and care for yourself while supporting someone else.\n\nYou don\u2019t need to complete everything today. We\u2019ll guide you one step at a time.',
    href: null, primary: 'Start tour',
  },
  {
    icon: Home, kicker: 'Step 1 · Support Home',
    title: 'Your starting point',
    body: 'This is your starting point. Every visit begins here.\n\nYou\u2019ll receive practical guidance, featured resources, and recommendations based on the situations supporters commonly face.',
    href: '/app/support-home',
  },
  {
    icon: MessageCircleHeart, kicker: 'Step 2 · Support Coach',
    title: 'Talk through difficult situations',
    body: 'Talk through difficult situations with your AI Support Coach.\n\nWhether you\u2019re unsure what to say, worried about someone, or simply need guidance, your coach provides thoughtful, evidence-informed support.\n\nIt never diagnoses or replaces professional care.',
    href: '/app/support/coach',
  },
  {
    icon: BookOpen, kicker: 'Step 3 · Support Library',
    title: 'Practical guides written for you',
    body: 'The Support Library contains practical guides written for partners, parents, siblings, friends, mentors, pastors, and coaches.\n\nTopics include difficult conversations, anger, grief, crisis, boundaries, therapy resistance, and self-care.',
    href: '/app/support',
  },
  {
    icon: LifeBuoy, kicker: 'Step 4 · Crisis Help',
    title: 'Immediate resources when it matters',
    body: 'If someone may be in immediate danger, Brotherhood Support gives you quick access to emergency resources including 911, 988, crisis text support, and additional support services.',
    href: '/app/support/sup_crisis',
  },
  {
    icon: UserCircle, kicker: 'Step 5 · Your Profile',
    title: 'Preferences & account',
    body: 'Manage your account, subscription, preferences, notifications, and language settings.',
    href: '/app/profile',
  },
];

const MEMBER_FINISH = {
  icon: Sparkles,
  title: 'You\u2019re ready.',
  body: 'You don\u2019t have to do everything today. Simply complete today\u2019s recommendation — Brotherhood+ will continue guiding your journey from there.',
  cta: 'Go to my dashboard',
  href: '/app',
};

const SUPPORTER_FINISH = {
  icon: HeartHandshake,
  title: 'You\u2019re ready.',
  body: 'Supporting someone is not about having all the answers. It is about showing up with compassion, patience, honesty, and healthy boundaries.\n\nBrotherhood Support is here whenever you need guidance.',
  cta: 'Explore Brotherhood Support',
  href: '/app/support-home',
};

export default function OnboardingTour() {
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const isSupporter = (user?.role || 'member') === 'supporter';
  const STEPS = isSupporter ? SUPPORTER_STEPS : MEMBER_STEPS;
  const FINISH = isSupporter ? SUPPORTER_FINISH : MEMBER_FINISH;

  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    http.get('/me/tour-status').then((r) => {
      setStatus(r.data);
      if (!r.data.completed) setOpen(true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Global replay listener (from Help Center / Profile / HelpButton).
    const handler = () => { setStep(0); setOpen(true); };
    window.addEventListener('bp:replay-tour', handler);
    return () => window.removeEventListener('bp:replay-tour', handler);
  }, []);

  const close = async () => {
    try { await http.post('/me/complete-tour'); } catch { /* ignore */ }
    setOpen(false);
  };

  const finishAndGo = async () => {
    setFinishing(true);
    try { await http.post('/me/complete-tour'); } catch { /* ignore */ }
    setOpen(false);
    nav(FINISH.href);
  };

  if (status?.completed && !open) return null;
  if (!open) return null;

  const isFinish = step >= STEPS.length;
  const s = isFinish ? FINISH : STEPS[step];
  const isFirst = step === 0;
  const total = STEPS.length;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 lg:p-8"
      style={{ backgroundColor: 'rgba(11,25,44,0.72)' }}
      data-testid="onboarding-tour"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md lg:max-w-xl relative rounded-3xl"
          style={{ backgroundColor: '#FFFFFF', boxShadow: '0 40px 100px -20px rgba(11,25,44,0.5)' }}
        >
          {!isFinish && (
            <button
              data-testid="tour-close" onClick={close}
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-navy/50 hover:text-navy hover:bg-beige transition-smooth"
              aria-label="Skip tour"
            >
              <X size={16}/>
            </button>
          )}

          <div className="p-7 lg:p-10">
            <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: 'rgba(197,160,89,0.14)' }}>
              <s.icon size={26} className="text-gold" strokeWidth={1.5}/>
            </div>

            {!isFinish ? (
              <>
                <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">{s.kicker}</p>
                <div className="flex items-center gap-2 text-[11px] text-navy/45 mt-1">
                  <span>Step {step + 1} of {total}</span>
                  <span className="w-1 h-1 rounded-full bg-navy/20"/>
                  <span className="inline-flex items-center gap-1"><Clock size={10} strokeWidth={1.75}/>Under 2 min</span>
                </div>
              </>
            ) : (
              <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Finish</p>
            )}

            <h2 className="font-heading text-2xl lg:text-3xl text-navy mt-2 leading-snug">{s.title}</h2>
            <p className="text-sm lg:text-base text-navy/70 mt-3 leading-relaxed whitespace-pre-line">{s.body}</p>

            {/* progress dots (hidden on finish) */}
            {!isFinish && (
              <div className="flex items-center gap-1.5 mt-6">
                {STEPS.map((_, i) => (
                  <span
                    key={i}
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{
                      width: i === step ? 32 : 16,
                      backgroundColor: i === step ? '#C5A059' : i < step ? 'rgba(11,25,44,0.4)' : 'rgba(11,25,44,0.1)',
                    }}
                  />
                ))}
              </div>
            )}

            {/* controls */}
            <div className="mt-7 flex items-center justify-between gap-3">
              {!isFinish ? (
                <>
                  <button
                    data-testid="tour-skip" onClick={close}
                    className="text-sm text-navy/50 hover:text-navy transition-smooth"
                  >Skip tour</button>

                  <div className="flex items-center gap-2">
                    {!isFirst && (
                      <button
                        data-testid="tour-prev" onClick={() => setStep(step - 1)}
                        className="inline-flex items-center gap-1.5 text-sm bg-beige text-navy rounded-full px-4 py-2.5 hover:bg-[#EAE5D9] transition-smooth"
                      >
                        <ArrowLeft size={13}/>Back
                      </button>
                    )}
                    <button
                      data-testid="tour-next" onClick={() => setStep(step + 1)}
                      className="inline-flex items-center gap-2 text-sm bg-navy text-white rounded-full px-5 py-2.5 hover:bg-navy-soft transition-smooth"
                    >
                      {isFirst ? 'Start tour' : (step === total - 1 ? 'Finish' : 'Next')} <ArrowRight size={14}/>
                    </button>
                  </div>
                </>
              ) : (
                <button
                  data-testid="tour-finish" onClick={finishAndGo} disabled={finishing}
                  className="w-full inline-flex items-center justify-center gap-2 text-sm bg-gold text-white rounded-full px-6 py-3 hover:bg-gold-hover transition-smooth disabled:opacity-40"
                >
                  {FINISH.cta} <ArrowRight size={14}/>
                </button>
              )}
            </div>

            {/* Optional: jump to page (non-finish steps that have href) */}
            {!isFinish && s.href && !isFirst && (
              <button
                data-testid="tour-visit-page"
                onClick={async () => { await close(); nav(s.href); }}
                className="mt-3 text-xs text-navy/50 hover:text-navy inline-flex items-center gap-1 transition-smooth"
              >Open this page now <ArrowRight size={11}/></button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
